import React, {
    useCallback, useContext, useMemo, useRef, useState
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { Button, Link } from '@jutro/components';
import SgCreditCardDetails from 'sg-credit-card-details';
import { SgDirectDebitDetails, SgDirectDebitForm } from 'sg-direct-debit-details';
import { useValidation } from 'gw-portals-validation-react';
import {
    SgPaymentUtils, ADOBE_TOPICS, COMMON_TAGGING_PROPERTIES, SgWebchatUtil
} from 'saga-modules';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import SgFailedDownloadPayment from 'sg-failed-download-payment';
import SgScrollUtil from 'sg-scroll-util';
import SgCepLobSpecificUtil from 'sg-cep-lob-specific-util';
import { publish } from '@jutro/events';
import messages from '../SgPolicyPaymentTile.messages';
import SgNewCardDetails from '../../sg-new-card';

function SgPaymentTileInstrumentDetails(props) {
    const {
        id,
        paymentDetails,
        setToPayFilterSelected,
        setPaidFilterSelected,
        setShowPaymentInstrument,
        paymentInstrumentVM,
        setPaymentInstrumentVM,
        editAutoRenewal,
        setEditAutoRenewal,
        policyNumber,
        productCode,
        isInRenewalCycle,
        isRenewable,
        customerDetails
    } = props;

    const {
        onValidate,
        isComponentValid,
    } = useValidation(id);

    const translator = useContext(TranslatorContext);

    const CARD_VERIFICATION_AMOUNT = 0.00;
    const existingPaymentInstrument = useRef(_.cloneDeep(paymentInstrumentVM.value));
    const showDirectDebit = !_.isNil(paymentDetails) && SgPaymentUtils.isDirectDebit(paymentDetails.paymentInstrument.paymentMethod);
    const showCreditCard = !_.isNil(paymentDetails) && SgPaymentUtils.isCreditCardPaymentMethod(paymentDetails.paymentInstrument.paymentMethod);
    const [showChangeDetailsForm, setShowChangeDetailsForm] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showFailedDownloadMsg, setShowFailedDownloadMsg] = useState(false);
    const [showConfirmationMsg, setShowConfirmationMsg] = useState(false);

    const commonProps = {
        paymentDetails,
        paymentInstrumentVM,
        setPaymentInstrumentVM,
        setEditAutoRenewal,
        editAutoRenewal,
        onValidate,
        updateData: setPaymentInstrumentVM,
        showErrors,
        setShowChangeDetailsForm,
        existingPaymentInstrument,
        isInRenewalCycle,
        isRenewable,
        productCode
    };

    const addInstrument = useCallback(() => {
        return AccountBillingDetailsService.setNewPaymentInstrument(paymentDetails.internalId, paymentInstrumentVM.value).then(() => {
            setShowChangeDetailsForm(false);
        }).catch((err) => {
            _.set(err, 'customErrorMessage', 'Could not set new payment instrument');
            throw err;
        });
    }, [paymentDetails, paymentInstrumentVM]);

    const validateBankAccount = useCallback(() => {
        if (!_.isEqual(paymentInstrumentVM.value, existingPaymentInstrument.current)) {
            paymentInstrumentVM.creditCardData = null;
            paymentInstrumentVM.value.quoteID = policyNumber;
            SgPaymentUtils.bankAccountValidation(paymentInstrumentVM, {}, addInstrument, productCode);
        } else {
            setShowChangeDetailsForm(false);
        }
    }, [addInstrument, paymentInstrumentVM, policyNumber, productCode]);

    const updatePaymentDetails = useCallback(() => {
        if (!isComponentValid) {
            setShowErrors(true);
            return;
        }
        if (!showDirectDebit) {
            addInstrument();
        } else {
            validateBankAccount();
        }
    }, [addInstrument, isComponentValid, showDirectDebit, validateBankAccount]);

    const updateDisplayedInfo = useCallback((newCardDetails) => {
        setShowConfirmationMsg(true);
        paymentInstrumentVM.creditCardData.creditCardNumber.value = newCardDetails.pan;
        paymentInstrumentVM.creditCardData.creditCardHolder_Ext.value = newCardDetails.cardHolderName;
        paymentInstrumentVM.creditCardData.creditCardExpDate.value = newCardDetails.expiryDate;
        paymentInstrumentVM.creditCardData.autoRenewal_Ext.value = newCardDetails.cpauthorisation;
        setPaymentInstrumentVM(paymentInstrumentVM);
    }, [paymentInstrumentVM, setPaymentInstrumentVM]);

    const replaceCard = useCallback((barclayResponse) => {
        const newCardDetails = _.clone(barclayResponse);
        updateDisplayedInfo(newCardDetails);
    }, [updateDisplayedInfo]);

    const trackChat = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        SgWebchatUtil.trackChat();
    }, []);

    const confirmationMessage = useMemo(() => {
        SgScrollUtil.scrollToElementById(`policyPaymentContainer_${id}`, null, -50);
        return (
            <div>
                <h2 id="confirmationMessage">
                    <i className="mir mi-check" />
                    {translator(messages.DetailsUpdated)}
                </h2>
                <div className="sgBottomButtonsContainer">
                    <Button
                        id="backToPayments"
                        onClick={() => { setShowPaymentInstrument(false); setToPayFilterSelected(true); setPaidFilterSelected(true); }}
                        className="sgButton sgSecondaryButton"
                    >
                        {translator(messages.BackToPayments)}
                    </Button>
                </div>
            </div>
        );
    }, [id, setPaidFilterSelected, setShowPaymentInstrument, setToPayFilterSelected, translator]);

    const shouldShowChangeDetailsButton = useMemo(() => {
        return SgCepLobSpecificUtil.shouldShowChangePaymentDetailsButton(paymentDetails, productCode, isRenewable);
    }, [productCode, paymentDetails, isRenewable]);

    const changeDetailsButton = useMemo(() => {
        if (isInRenewalCycle) {
            publish(ADOBE_TOPICS.ADOBE_EVENT, {
                adobeID: COMMON_TAGGING_PROPERTIES.changePaymentDetailsCepBillingPageIsDisabled,
                value: true,
            });
        }
        return (
            <Button
                id={`changePaymentDetails_${id}`}
                onClick={() => setShowChangeDetailsForm(true)}
                className="sgButton"
                disabled={isInRenewalCycle}
            >
                {translator(messages.ChangeMyPaymentDetails)}
            </Button>
        );
    }, [id, isInRenewalCycle, translator]);

    return (
        <div id={`policyPaymentInstrument_${id}`}>
            <div className="sgCepPaymentsInstrumentContainer">
                { !showChangeDetailsForm
                && (
                    <>
                        <h2>{translator(messages.CurrentPaymentDetails)}</h2>
                        { showDirectDebit && !_.isNil(paymentDetails.paymentInstrument.bankAccountData)
                            && <SgDirectDebitDetails {...commonProps} />
                        }
                        { showCreditCard && !_.isNil(paymentDetails.paymentInstrument.creditCardData)
                            && <SgCreditCardDetails {...commonProps} />
                        }
                        {shouldShowChangeDetailsButton && isInRenewalCycle && (
                            <div className="sgCepPaymentsInstrumentWarningPanel">
                                <span>{translator(messages.ChangePaymentDetailsTooltip)}</span>
                                <Link
                                    href="/"
                                    id="webchatLink"
                                    onClick={trackChat}
                                    className="sgWebchatLink saga-intercom"
                                    aria-hidden="true"
                                >
                                    {translator(messages.WebChatLink)}
                                </Link>
                            </div>
                        )}
                        <div className="sgBottomButtonsContainer">
                            <Button
                                id={`backToInvoices_${id}`}
                                onClick={() => { setShowPaymentInstrument(false); setToPayFilterSelected(true); setPaidFilterSelected(true); }}
                                className="sgButton sgSecondaryButton"
                            >
                                {translator(messages.BackToPayments)}
                            </Button>
                            {shouldShowChangeDetailsButton && changeDetailsButton }
                        </div>
                    </>
                )}
                { showChangeDetailsForm
                && (
                    <>
                        { !showConfirmationMsg ? (
                            <>
                                <h2>{translator(messages.ChangePaymentDetailsHeader)}</h2>
                                { showDirectDebit && <SgDirectDebitForm {...commonProps} /> }
                                { showCreditCard
                                && (
                                    <>
                                        <SgFailedDownloadPayment showFailedDownloadMsg={showFailedDownloadMsg} />
                                        <SgNewCardDetails
                                            paymentDetails={paymentDetails}
                                            productCode={productCode}
                                            customerDetails={customerDetails}
                                            amount={CARD_VERIFICATION_AMOUNT.toFixed(2)}
                                            bind={replaceCard}
                                            setShowFailedDownloadMsg={setShowFailedDownloadMsg}
                                        />
                                    </>
                                )}
                                <div className="sgBottomButtonsContainer">
                                    <Button
                                        id={`backToCurrentDetails_${id}`}
                                        onClick={() => SgPaymentUtils.cancelUpdatingPaymentDetails(commonProps)}
                                        className="sgButton sgSecondaryButton"
                                    >
                                        {translator(messages.Cancel)}
                                    </Button>
                                    { showDirectDebit && (
                                        <Button
                                            id={`updatePaymentDetails_${id}`}
                                            onClick={() => updatePaymentDetails()}
                                            className="sgButton"
                                        >
                                            {translator(messages.UpdateMyPaymentDetails)}
                                        </Button>
                                    )}
                                </div>
                            </>
                        ) : confirmationMessage }
                    </>
                )}
                <div className="sgClearBoth">&nbsp;</div>
            </div>
        </div>
    );
}

export default SgPaymentTileInstrumentDetails;
